'use client';

import { useState, useEffect, useCallback } from 'react';
import Flex from '~/components/customComponents/flex/Flex';
import { usePostHog } from '~/hooks/usePostHog';
import RetryResetPassword from './RetryResetPassword';
import ResetPassword from './ResetPassword';
import { LocaleEnum } from '~/locales/resources';

/**
 * A functional component that handles the forgot password flow.
 * It allows the user to reset their password or request a new one and sets `shouldProgress` to true.
 * Then shows the retry reset password component, so that the user can try again if no email recieved.
 * @param {LocaleEnum} locale - The locale of the current user.
 * @return {JSX.Element} The JSX element representing the forgot password form.
 */
const ForgotPassword = ({ locale }: { locale: LocaleEnum }) => {
  const [email, setEmail] = useState('');
  const [shouldProgress, setShouldProgress] = useState(false);
  const [isSendButtonLoading, setIsSendButtonLoading] = useState(false);
  const { captureEvent } = usePostHog();

  useEffect(() => {
    setShouldProgress(false);
  }, [email]);

  const onEmailChange = useCallback((value: string) => setEmail(value?.toLocaleLowerCase()), []);

  const redirectToForm = () => setShouldProgress(false);

  const resetPassword = async () => {
    const trimmedEmail = email.trim();
    const response = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email: trimmedEmail, locale }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  };

  const handleResetSubmit = async () => {
    setIsSendButtonLoading(true);
    captureEvent('password_reset_requested', { email: email.trim() });

    await resetPassword();

    setTimeout(() => {
      setShouldProgress(true);
      setIsSendButtonLoading(false);
    }, 500);
  };

  const isCTADisabled = Boolean(!email?.length);

  const handleFormResetSubmit = async (
    event: React.MouseEvent<HTMLFormElement> | React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    handleResetSubmit();
  };

  return (
    <Flex className="flex-col w-full md:w-96 mx-auto">
      {shouldProgress ? (
        <RetryResetPassword
          email={email}
          redirectToForm={redirectToForm}
          onButtonClick={handleResetSubmit}
          isSendButtonLoading={isSendButtonLoading}
        />
      ) : (
        <ResetPassword
          email={email}
          isCTADisabled={isCTADisabled}
          onEmailChange={onEmailChange}
          handleFormResetSubmit={handleFormResetSubmit}
          isSendButtonLoading={isSendButtonLoading}
        />
      )}
    </Flex>
  );
};

export default ForgotPassword;
